import { CheckInOption, DefaultCheckInOption } from 'store/building-settings/types';
import type { TenantBuildingSettingsOverride } from 'store/building-settings/types';
import type { VisitTypeData } from 'store/building/types';

const normalizeCode = (code: string | undefined | null): string => (code ?? '').replace(/-/g, '_').toUpperCase();

const codeForDefault = (defaultOption: DefaultCheckInOption): 'STOP_AT_DESK' | 'BYPASS_DESK' | null => {
  if (defaultOption === DefaultCheckInOption.STOP_AT_DESK) {
    return 'STOP_AT_DESK';
  }
  if (defaultOption === DefaultCheckInOption.BYPASS_DESK) {
    return 'BYPASS_DESK';
  }
  return null;
};

/**
 * Whether the check-in radio picker is hidden for this building/tenant. When
 * hidden, we _must_ seed a valid visit type id — otherwise the create request
 * omits `visit_type_id` and the persisted visit becomes uneditable elsewhere.
 */
export const isCheckInPickerHidden = (
  availableCheckInOptions: CheckInOption | null | undefined,
  tenantOverride: TenantBuildingSettingsOverride | undefined,
): boolean => {
  const effective = tenantOverride?.availableCheckInOptions ?? availableCheckInOptions;
  return effective === CheckInOption.STOP_AT_DESK || effective === CheckInOption.BYPASS_DESK;
};

/**
 * Resolves the visit type that should be selected as the default on the
 * create form when the user has not yet made an explicit choice.
 *
 * Preference order:
 * 1. A visit type whose `code` matches the effective default check-in option
 *    (STOP_AT_DESK / BYPASS_DESK), with dashed codes like `stop-at-desk`
 *    treated as equivalent to `STOP_AT_DESK`.
 * 2. When the picker is hidden by `availableCheckInOptions` (STOP_AT_DESK or
 *    BYPASS_DESK only) but no matching code exists at this location, fall
 *    back to the first configured visit type. This prevents the create
 *    request from being submitted without a `visit_type_id`, which would
 *    otherwise persist as null and break the edit flow.
 */
export const resolveDefaultVisitType = (
  visitTypes: VisitTypeData[] | null | undefined,
  effectiveDefault: DefaultCheckInOption | null | undefined,
  availableCheckInOptions: CheckInOption | null | undefined,
  tenantOverride: TenantBuildingSettingsOverride | undefined,
): VisitTypeData | undefined => {
  if (!visitTypes || visitTypes.length === 0) {
    return undefined;
  }

  if (effectiveDefault && effectiveDefault !== DefaultCheckInOption.NONE) {
    const targetCode = codeForDefault(effectiveDefault);
    if (targetCode) {
      const byCode = visitTypes.find((vt) => normalizeCode(vt.code) === targetCode);
      if (byCode) {
        return byCode;
      }
    }
  }

  if (isCheckInPickerHidden(availableCheckInOptions, tenantOverride)) {
    return visitTypes[0];
  }

  return undefined;
};
